import { Stack, Tag } from "@carbon/react";
import { useModal } from "@flaminem/ui-components";
import { FC } from "react";
import { Link } from "react-router-dom";

import { AuthorizedItem } from "src/authentication/AuthorizedMenu.tsx";
import { Scope, ScopeResource } from "src/authentication/PermissionsProvider.tsx";
import { AlertStatusTag } from "src/components/Alerts/AlertStatusTag.tsx";
import { ModalTransition } from "src/components/Modal";
import { AttachmentsModal } from "src/dynamic-view-renderer/Widgets/AlertWorkflowWidget/AttachmentsModal.tsx";
import { CommentModal } from "src/dynamic-view-renderer/Widgets/AlertWorkflowWidget/CommentModal.tsx";
import {
  BasicWidgetHorizontalProperty,
  BasicWidgetHorizontalPropertyLayout,
} from "src/dynamic-view-renderer/Widgets/BasicWidget/BasicWidgetHorizontal.tsx";
import {
  BasicWidgetVerticalProperty,
  BasicWidgetVerticalPropertyLayout,
} from "src/dynamic-view-renderer/Widgets/BasicWidget/BasicWidgetVertical.tsx";
import { getScenarioVersionUrl } from "src/dynamic-view-renderer/Widgets/ExecutionsWidget/getScenarioVersionUrl.ts";
import WidgetContainer, {
  WidgetContainerProps,
} from "src/dynamic-view-renderer/Widgets/WidgetContainer.tsx";
import { AttachmentsApi } from "src/http/Attachments.api.ts";
import { useFormatDate, useT } from "src/internationalization";
import { useCurrentUser } from "src/providers/CurrentUserProvider.tsx";
import { Alert } from "src/types/alert.ts";

import AssignAlertButton from "./AssignAlertButton.tsx";

export type AlertWorkflowWidgetProps = {
  data?: {
    alert?: Alert | null;
    refetch: () => void;
  };
} & Omit<WidgetContainerProps, "children">;

const AlertWorkflowWidget: FC<AlertWorkflowWidgetProps> = ({ data, ...containerProps }) => {
  const alert = data?.alert;
  const t = useT();
  const formatDate = useFormatDate();
  const {
    isModalOpen: isCommentModalOpen,
    openModal: openCommentModal,
    closeModal: closeCommentModal,
  } = useModal();

  const {
    isModalOpen: isFilesModalOpen,
    openModal: openFilesModal,
    closeModal: closeFilesModal,
  } = useModal();

  const { currentUser } = useCurrentUser();

  if (!alert) return <WidgetContainer {...containerProps}>{""}</WidgetContainer>;

  return (
    <>
      <WidgetContainer {...containerProps}>
        <Stack gap={5}>
          <BasicWidgetHorizontalPropertyLayout>
            <BasicWidgetHorizontalProperty
              label={t("component.widget.alert.workflow.status")}
              value={<AlertStatusTag status={alert.status} />}
            />
            {alert.origin !== "manual" && (
              <BasicWidgetHorizontalProperty
                label={t("component.widget.alert.workflow.score")}
                value={
                  <Tag type="red" className="!mx-0">
                    {alert.globalScore}
                  </Tag>
                }
              />
            )}
            <BasicWidgetHorizontalProperty
              label={t("component.widget.alert.workflow.assigned")}
              value={
                <div>
                  <p className="text-sm" style={{ flex: "0 1 auto" }}>
                    {alert.assigneeFullName}
                  </p>
                  {alert && !alert.isAlertValidated && alert.assignee?.id !== currentUser.id && (
                    <p className="text-sm mt-3" style={{ flex: "0 1 auto" }}>
                      <AssignAlertButton alertId={alert.id} />
                    </p>
                  )}
                </div>
              }
            />
            {/*TODO: Scenario page does not exist, update when it is available*/}
            {alert.scenarioExecutionResult && (
              <BasicWidgetHorizontalProperty
                label={t("component.widget.alert.workflow.scenario")}
                value={
                  getScenarioVersionUrl(alert.scenarioExecutionResult) && (
                    <Link
                      to={getScenarioVersionUrl(alert.scenarioExecutionResult) ?? ""}
                      className="no-underline"
                    >
                      {alert.scenarioExecutionResult?.scenarioVersion?.scenario?.name} -{" "}
                      {alert.scenarioExecutionResult?.scenarioVersion?.name}
                    </Link>
                  )
                }
              />
            )}
          </BasicWidgetHorizontalPropertyLayout>
          <BasicWidgetVerticalPropertyLayout>
            {alert.validationDate && (
              <BasicWidgetVerticalProperty
                label={t("component.widget.alert.workflow.validation.date")}
                value={formatDate(alert.validationDate, {
                  dateStyle: "medium",
                })}
              />
            )}
            {alert.fraudType && (
              <BasicWidgetVerticalProperty
                label={t("component.widget.alert.workflow.fraud.type")}
                value={alert.fraudType}
              />
            )}

            <div className="w-full flex justify-between py-5 items-center gap-3 flex-wrap border-b border-solid border-borderSubtle01">
              <p className="text-sm text-textSecondary flex gap-4" style={{ flex: "1 1 auto" }}>
                <span>{t("component.widget.alert.workflow.attachments")}</span>
                {!alert.isAlertValidated && (
                  <span>
                    <AuthorizedItem
                      requiredPermissions={[
                        { resource: ScopeResource.ALERT, scopes: [Scope.EDIT] },
                      ]}
                    >
                      <Link to="" className="no-underline" onClick={() => openFilesModal()}>
                        {alert.attachments.length
                          ? t("page.alert.tab.synthesis.widget.alert.attachments.show")
                          : t("page.alert.tab.synthesis.widget.alert.attachments.add")}
                      </Link>
                    </AuthorizedItem>
                  </span>
                )}
              </p>
              <p
                className="text-sm line-clamp-[7] overflow-hidden hover:overflow-auto"
                style={{ flex: "0 1 auto", scrollbarGutter: "stable" }}
              >
                {alert.attachments.map((attachment) => (
                  <p key={attachment.id}>
                    <Link
                      to={""}
                      onClick={() => AttachmentsApi.Attachments().getAttachment(attachment)}
                    >
                      {attachment.name}
                    </Link>
                  </p>
                ))}
              </p>
            </div>
            <div className="w-full flex justify-between py-5 items-center gap-3 flex-wrap">
              <p className="text-sm text-textSecondary flex gap-4" style={{ flex: "1 1 auto" }}>
                <span>{t("component.widget.alert.workflow.comment")}</span>
                {!alert.isAlertValidated && (
                  <span>
                    <AuthorizedItem
                      requiredPermissions={[
                        { resource: ScopeResource.ALERT, scopes: [Scope.EDIT] },
                      ]}
                    >
                      <Link to="" className="no-underline" onClick={() => openCommentModal()}>
                        {alert.comment
                          ? t("general.actions.edit")
                          : t("page.alert.tab.synthesis.widget.alert.comment.add")}
                      </Link>
                    </AuthorizedItem>
                  </span>
                )}
              </p>
              <p
                className="text-sm line-clamp-[7] overflow-hidden hover:overflow-auto"
                style={{ flex: "0 1 auto", scrollbarGutter: "stable" }}
              >
                {alert.comment}
              </p>
            </div>
          </BasicWidgetVerticalPropertyLayout>
        </Stack>
      </WidgetContainer>
      <ModalTransition>
        {isCommentModalOpen && (
          <CommentModal
            alertId={alert.id}
            alertNumber={alert.alertNumber}
            onClose={closeCommentModal}
            comment={alert.comment}
          />
        )}
      </ModalTransition>
      <ModalTransition>
        {isFilesModalOpen && (
          <AttachmentsModal
            alertId={alert.id}
            alertNumber={alert.alertNumber}
            onClose={() => {
              data?.refetch();
              closeFilesModal();
            }}
            refreshList={() => data?.refetch()}
            attachments={alert.attachments}
          />
        )}
      </ModalTransition>
    </>
  );
};

export default AlertWorkflowWidget;
